import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";
import { ToastContainer } from "react-toastify";
import { Dropdown } from "react-bootstrap";

import logo from "../../assets/images/logo.svg";
import RegionSelector from "../RegionSelector";
import { clearRecentUsers } from "../../helpers/recentUsers";
import { useAllowedRoutes } from "../RoleBasedAccessControl";
import styles from "./index.module.scss";

interface LayoutProps {
  signOut?: () => void;
}

type NavChild = {
  pathname: string;
  title: string;
};

type NavRoute = {
  pathname?: string;
  title: string;
  children?: NavChild[];
};

const routePath: NavRoute[] = [
  { pathname: "/", title: "Dashboard" },
  { pathname: "/users", title: "Users" },
  {
    title: "Accounting",
    children: [
      { pathname: "/camp-transactions", title: "CAMP Activity" },
      { pathname: "/ledger-activity", title: "Ledger Activity" },
      { pathname: "/ledger-balances", title: "Ledger Balances" },
      { pathname: "/journal-entries", title: "Journal Entries" },
    ],
  },
];

const leafPathnames = routePath.flatMap((route) =>
  route.children
    ? route.children.map((child) => child.pathname)
    : route.pathname
    ? [route.pathname]
    : []
);

const navTestId = (pathname: string) =>
  `nav-${pathname.replace(/^\//, "") || "dashboard"}`;

const NavMenu = ({ route }: { route: NavRoute }) => {
  const location = useLocation();

  if (!route.children) {
    return (
      <NavLink
        to={route.pathname!}
        className={styles.navListLabel}
        style={({ isActive }) => (isActive ? { color: "#E42082" } : undefined)}
        end={route.pathname === "/"}
        data-testid={navTestId(route.pathname!)}
      >
        <p>{route.title}</p>
      </NavLink>
    );
  }

  const isActive = route.children.some(
    (child) => location.pathname === child.pathname
  );

  return (
    <Dropdown className={styles.navDropdown}>
      <Dropdown.Toggle
        variant="link"
        id="nav-accounting-toggle"
        className={styles.navDropdownToggle}
        style={isActive ? { color: "#E42082" } : undefined}
        data-testid="nav-accounting"
      >
        <span>{route.title}</span>
        <span className="material-icons material-icons-outlined">
          expand_more
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.navDropdownMenu}>
        {route.children.map((child) => (
          <Dropdown.Item
            as={NavLink}
            to={child.pathname}
            key={child.pathname}
            className={styles.navDropdownItem}
            data-testid={navTestId(child.pathname)}
          >
            {child.title}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default function Layout(props: LayoutProps) {
  const { allowedRoutes, isLoading } = useAllowedRoutes(leafPathnames);
  const visibleRoutes = routePath.flatMap((route) => {
    if (!route.children) {
      return route.pathname && allowedRoutes.includes(route.pathname)
        ? [route]
        : [];
    }

    const children = route.children.filter((child) =>
      allowedRoutes.includes(child.pathname)
    );
    return children.length > 0 ? [{ ...route, children }] : [];
  });

  if (isLoading) {
    return (
      <main className={styles.container}>
        <div data-testid="nav-loading-indicator">Loading...</div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <nav className={styles.navContainer}>
        <ReactNotifications />
        <ToastContainer />
        <Link to="/">
          <img className={styles.logo} src={logo} alt="logo of Chocolate" />
        </Link>
        <div className={styles.navList}>
          {visibleRoutes.map((route) => (
            <NavMenu key={route.title} route={route} />
          ))}
        </div>
        <RegionSelector />
        <Link
          to="#"
          className={styles.navLink}
          onClick={() => {
            clearRecentUsers();
            props.signOut?.();
          }}
          data-testid="nav-sign-out-button"
        >
          <span className="material-icons material-icons-outlined">logout</span>
          <p className={styles.navLinkLabel}>Sign Out</p>
        </Link>
      </nav>
      <div className={styles.outletContainer}>
        <Outlet />
      </div>
    </main>
  );
}
